import { getCurrentTimestamp } from '@zenid/ts-lib';
import { isAfter, isBefore } from 'date-fns';
import type { GroupSchema, MembershipSchema, RoleAssigningSchema } from './schema';

/**
 * from が過去、かつ to が未設定もしくは未来なら有効
 */
export function isGroupActive(group: GroupSchema | undefined, baseTime = getCurrentTimestamp()) {
  if (!group) {
    return false;
  }
  // from が現在時刻より後なら無効
  if (isAfter(group.from, baseTime)) {
    return false;
  }

  // to が無ければ有効
  if (!group.to) {
    return true;
  }

  // to が現在時刻より前なら有効、後なら無効
  return isAfter(group.to, baseTime);
}

/**
 * leftAt と joinedAt から計算する
 */
export function isMembershipActive(
  membership: MembershipSchema | undefined,
  baseTime = getCurrentTimestamp()
): boolean {
  if (!membership) {
    return false;
  }
  // joinedAt が未来なら無条件で false
  if (isAfter(membership.joinedAt, baseTime)) {
    return false;
  }
  // leftAt が未設定なら有効
  if (!membership.leftAt) {
    return true;
  }
  // leftAt が未来なら有効
  if (isAfter(membership.leftAt, baseTime)) {
    return true;
  }

  // leftAt が過去であるため無効
  return false;
}

/**
 * from が過去かつ to が未設定なら有効
 * 開始日が未来、または終了日が過去の場合は無効
 * suspend されている場合にも to は入るため、 suspend 関連の条件判定を行う場合は isRoleSuspended を併せて使用する
 */
export function isRoleActive(
  roleAssigning: RoleAssigningSchema | undefined,
  baseTime = getCurrentTimestamp()
): boolean {
  if (!roleAssigning) {
    return false;
  }
  if (roleAssigning.from && isBefore(baseTime, roleAssigning.from)) {
    return false;
  }
  if (roleAssigning.to && isBefore(roleAssigning.to, baseTime)) {
    return false;
  }
  return true;
}

export function isRoleSuspended(roleAssigning: RoleAssigningSchema, baseTime = getCurrentTimestamp()): boolean {
  // 有効な suspending が存在したら true
  return roleAssigning.suspendings.some((s) => isActiveBasedOnTime(s, baseTime));
}

/**
 * from / to ベースで有効性を確認する抽象実装
 * TODO: isXxxActive 系を置き換える
 */
export function isActiveBasedOnTime<T extends { from: number; to?: number }>(
  t: T | undefined,
  baseTime = getCurrentTimestamp()
): boolean {
  if (!t) {
    return false;
  }
  // from が未到来なら無条件で false
  if (t.from && isBefore(baseTime, t.from)) {
    return false;
  }
  // to が到来済なら無条件で false
  if (t.to && isBefore(t.to, baseTime)) {
    return false;
  }
  // それ以外は from が到来済、かつ to が未到来 or 未設定であり true
  return true;
}
